import React, { useCallback, useRef, useState } from 'react'
import { generatePath, useHistory } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'
import { ROUTES } from '../../routes'
import QUERY_KEYS from '../../graphql/queryKeys'
import useMutation from '../../hooks/useMutation'
import { generateQueryKey } from '../../hooks/useQuery'
import dismissTermsAndConditionWarningMutation from '../../graphql/mutations/dismissTermsAndConditionWarning'
import sendClientTermsAndConditionMutation from '../../graphql/mutations/sendClientTermsAndCondition'
import { captureErrorAndNotify } from '../../utilities/errorHandlers'
import { hasTermsAndConditionExpiredWarning } from '../../utilities/termsAndConditionWarnings'
import TermsAndConditionExpiredWarningModal from './index'

const WARNING_QUERY_KEYS = [
  QUERY_KEYS.client,
  QUERY_KEYS.clients,
  QUERY_KEYS.job,
  QUERY_KEYS.createTicketPage,
  QUERY_KEYS.ticket
]

export default function useTermsAndConditionExpiredWarning (client, kind) {
  const history = useHistory()
  const queryClient = useQueryClient()
  const userId = useSelector(({ user }) => user.user?.id)
  const pendingContinue = useRef(null)
  const actionInProgress = useRef(false)
  const [isOpen, setIsOpen] = useState(false)
  const [doNotShowAgain, setDoNotShowAgain] = useState(false)

  const clientId = client?.id
  const assignmentId = client?.termsAndConditionExpiredAssignmentId || null
  const warningActive = hasTermsAndConditionExpiredWarning(client, kind)

  const { mutateAsync: dismissWarning, isLoading: isDismissing } = useMutation(
    dismissTermsAndConditionWarningMutation,
    {
      onError (error) {
        captureErrorAndNotify(error, 'Unable to hide this warning')
      }
    }
  )

  const { mutateAsync: sendTermsAndCondition } = useMutation(
    sendClientTermsAndConditionMutation
  )

  const continuePending = useCallback(() => {
    const next = pendingContinue.current
    pendingContinue.current = null
    setIsOpen(false)
    if (typeof next === 'function') next()
  }, [])

  const warnOrContinue = useCallback((onContinue) => {
    if (!warningActive) {
      onContinue()
      return
    }

    pendingContinue.current = onContinue
    setDoNotShowAgain(false)
    setIsOpen(true)
  }, [warningActive])

  const handleDismiss = useCallback(() => {
    pendingContinue.current = null
    setDoNotShowAgain(false)
    setIsOpen(false)
  }, [])

  const handleDoNotShowAgainChange = useCallback((event) => {
    setDoNotShowAgain(event.target.checked)
  }, [])

  const runSelectedAction = useCallback(async (action) => {
    if (actionInProgress.current) return
    actionInProgress.current = true

    const closeModal = (runPending = false) => {
      setDoNotShowAgain(false)
      if (runPending) {
        continuePending()
      } else {
        pendingContinue.current = null
        setIsOpen(false)
      }
    }

    const maybeDismissWarning = async () => {
      if (!doNotShowAgain || !assignmentId) return

      try {
        await dismissWarning({ clientTermsAndConditionId: assignmentId })

        try {
          await Promise.all(
            WARNING_QUERY_KEYS.map((queryKey) => (
              queryClient.invalidateQueries(generateQueryKey([queryKey], userId))
            ))
          )
        } catch (error) {
          captureErrorAndNotify(error, 'Unable to refresh account warning status')
        }
      } catch {
        // A dismissal failure should not block the action the user selected.
      }
    }

    const maybeResendLink = () => {
      if (action !== 'resend' || !clientId) return

      // Creation starts immediately. The catch still reports a failed send after the page moves on.
      sendTermsAndCondition({ clientId }).catch((error) => {
        captureErrorAndNotify(error, 'Failed to send T&C link')
      })
    }

    try {
      await maybeDismissWarning()

      if (action === 'account') {
        closeModal(false)
        if (clientId) {
          history.push(generatePath(ROUTES.client, { clientId }))
        }
        return
      }

      maybeResendLink()
      closeModal(true)
    } finally {
      actionInProgress.current = false
    }
  }, [assignmentId, clientId, continuePending, dismissWarning, doNotShowAgain, history, queryClient, sendTermsAndCondition, userId])

  const handleCreateAnyways = useCallback(() => {
    runSelectedAction('create')
  }, [runSelectedAction])

  const handleCancelAndGoToAccount = useCallback(() => {
    runSelectedAction('account')
  }, [runSelectedAction])

  const handleCreateAndResend = useCallback(() => {
    runSelectedAction('resend')
  }, [runSelectedAction])

  const warningModal = (
    <TermsAndConditionExpiredWarningModal
      isOpen={isOpen}
      onCreateAnyways={handleCreateAnyways}
      onCancelAndGoToAccount={handleCancelAndGoToAccount}
      onCreateAndResend={handleCreateAndResend}
      onDismiss={handleDismiss}
      doNotShowAgain={doNotShowAgain}
      onDoNotShowAgainChange={handleDoNotShowAgainChange}
      isWorking={isDismissing}
      canDismiss={Boolean(assignmentId)}
    />
  )

  return { warnOrContinue, warningModal }
}
